/**
 * Route matcher for the anonymous read-only preview page (TIV-164;
 * TIV-254 adds the 6-digit draft-code form).
 *
 * Pure and dependency-free on purpose: main.tsx imports this statically
 * at the very top of bootstrap(), before any engine/sync module graph
 * loads, so it must cost nothing in the initial chunk.
 */

/**
 * Match `/p/{id}` where `{id}` is exactly 32 hex characters.
 * Returns the lowercased id, or `null` for any other path
 * (`/`, `/p`, `/p/`, `/p/short`, `/p/{id}/extra`, ...).
 * A single trailing slash is tolerated (`/p/{id}/` matches).
 */
export function matchPreviewPath(pathname: string): string | null {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
  const match = /^\/p\/([0-9a-fA-F]{32})$/.exec(normalized);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Match `/p/{id}` (32 hex — delegates to `matchPreviewPath`) OR
 * `/p/{code}` (exactly 6 digits, TIV-254), with the same trailing-slash
 * tolerance. Returns the source discriminated by kind, or `null` for
 * any other path.
 */
export function matchPreviewRoute(
  pathname: string,
): { kind: 'id'; value: string } | { kind: 'code'; value: string } | null {
  const id = matchPreviewPath(pathname);
  if (id !== null) return { kind: 'id', value: id };
  const normalized =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
  const match = /^\/p\/([0-9]{6})$/.exec(normalized);
  return match ? { kind: 'code', value: match[1] } : null;
}
